// @ts-nocheck
import * as React from "react";
import { Ic, siteName, brandParts, supportPhone, useCurrentUser, logout, AppFooter, PhoneInput, apiErrorMessage, fmtDuration } from "./common";
import { useEscapeKey } from "../components/useEscapeKey";
import { api } from "../api";

// 랜딩 상단 메뉴가 가리키는 공개 소개 페이지들.
//   /about          서비스 소개
//   /features       주요 기능
//   /care-act-2026  2026 통합돌봄법
//   /adopt          도입 안내      (도입 상담 접수 폼)
//   /support        고객지원        (FAQ + 문의 접수 폼)
// 전부 비로그인 공개(SCREEN_GUARDS 미등록). 헤더 내비는 PUBLIC_NAV 하나를 공유하며
// 랜딩(landing-desktop)도 같은 배열을 import 해 메뉴가 한 곳에서만 정의되게 한다.
// 문의 폼은 POST /api/v1/inquiries(공개) → 관리자 문의함(/inquiries)으로 들어간다.

const PUBLIC_NAV = [
  { href: "/about",         label: "서비스 소개" },
  { href: "/features",      label: "주요 기능" },
  { href: "/care-act-2026", label: "2026 통합돌봄법" },
  { href: "/adopt",         label: "도입 안내" },
  { href: "/support",       label: "고객지원" },
];

// ─── 모바일 햄버거 메뉴 ────────────────────────────────────
// 좁은 화면에서는 상단 메뉴 5개 + 로그인/회원가입이 한 줄에 들어가지 않는다.
// 랜딩(/)·공개 페이지·모바일 랜딩(/m)이 이 컴포넌트 하나를 공유해 메뉴 구성이
// 어디서나 같다(PUBLIC_NAV 단일 출처).
//
// 드로어는 position:fixed 다 — 실기기에서 화면 전체를 덮는 게 맞다. 데스크톱에서
// /m 을 폰 목업 프레임으로 볼 때는 프레임 밖까지 덮이지만, 그건 시안 미리보기 한정.

// 뷰포트가 기준폭 이하인지. 인라인 스타일로는 미디어쿼리를 쓸 수 없어 JS로 판단한다.
function useIsNarrow(maxWidth = 860) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = React.useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  );
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    // Safari 13 이하는 addEventListener 미지원 → addListener 폴백.
    if (mq.addEventListener) { mq.addEventListener("change", onChange); return () => mq.removeEventListener("change", onChange); }
    mq.addListener(onChange); return () => mq.removeListener(onChange);
  }, [query]);
  return narrow;
}

const MENU_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
       style={{width:20, height:20}}>
    <path d="M4 7h16M4 12h16M4 17h16"/>
  </svg>
);
const CLOSE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
       style={{width:18, height:18}}>
    <path d="M6 6l12 12M18 6L6 18"/>
  </svg>
);

// 메뉴별 한 줄 설명 — 드로어에서만 노출(터치 타깃이 커서 설명을 넣을 여유가 있다).
const NAV_DESC = {
  "/about":         "무엇을 어떻게 바꾸는 서비스인지",
  "/features":      "매칭·방문·기록·알림 기능 전체",
  "/care-act-2026": "시행 내용과 준비 사항",
  "/adopt":         "도입 절차와 상담 신청",
  "/support":       "자주 묻는 질문과 문의하기",
};

function MobileNavMenu({ active, compact = false }) {
  const { user } = useCurrentUser();
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(() => setOpen(false), []);
  useEscapeKey(close, open);

  // 드로어가 열린 동안 뒤 배경 스크롤을 잠근다.
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const btnSize = compact ? 34 : 40;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        aria-label="메뉴 열기" aria-expanded={open} aria-haspopup="menu"
        style={{width:btnSize, height:btnSize, display:'grid', placeItems:'center', borderRadius:12,
                border:'1px solid var(--border-soft)', background:'var(--surface)', color:'var(--ink-1)', cursor:'pointer'}}>
        {MENU_ICON}
      </button>

      {open && (
        <div role="dialog" aria-modal="true" aria-label="메뉴"
             style={{position:'fixed', inset:0, zIndex:200, display:'flex', justifyContent:'flex-end'}}>
          <div className="nav-overlay" onClick={close}
               style={{position:'absolute', inset:0, background:'rgba(26,22,18,0.45)'}}/>

          <aside className="nav-drawer"
            style={{position:'relative', width:'min(320px, 86vw)', height:'100%', background:'var(--surface)',
                    boxShadow:'-12px 0 40px rgba(20,16,12,0.18)', display:'flex', flexDirection:'column', overflowY:'auto'}}>
            <div style={{display:'flex', alignItems:'center', gap:10, padding:'16px 18px', borderBottom:'1px solid var(--border-soft)'}}>
              <div className="brand-mark" style={{width:28, height:28, fontSize:12}}>{brandParts(siteName()).mark}</div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontWeight:800, fontSize:13.5, color:'var(--ink-1)'}}>{brandParts(siteName()).name}</div>
                {brandParts(siteName()).sub && <div style={{fontSize:9.5, color:'var(--ink-3)', letterSpacing:'0.06em'}}>{brandParts(siteName()).sub}</div>}
              </div>
              <button type="button" onClick={close} aria-label="메뉴 닫기"
                style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10,
                        border:'1px solid var(--border-soft)', background:'var(--surface)', color:'var(--ink-2)', cursor:'pointer'}}>
                {CLOSE_ICON}
              </button>
            </div>

            {user && (
              <a href={user.default_path} onClick={close}
                 style={{display:'flex', alignItems:'center', gap:10, padding:'14px 18px', textDecoration:'none',
                         background:'var(--orange-50)', borderBottom:'1px solid var(--border-soft)'}}>
                <span style={{width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg, var(--orange-400), var(--orange-600))',
                              color:'#fff', display:'grid', placeItems:'center', fontSize:13, fontWeight:800}}>{(user.name || "?").charAt(0)}</span>
                <span style={{flex:1, minWidth:0}}>
                  <span style={{display:'block', fontSize:13, fontWeight:800, color:'var(--ink-1)'}}>{user.name}</span>
                  <span style={{display:'block', fontSize:11, color:'var(--ink-3)'}}>{user.role_label} · 내 화면으로 이동</span>
                </span>
                <span style={{color:'var(--orange-600)', width:14, height:14, display:'inline-block'}}>{Ic.arrow}</span>
              </a>
            )}

            <nav style={{padding:'8px 0', flex:1}}>
              {PUBLIC_NAV.map(n => {
                const on = active === n.href;
                return (
                  <a key={n.href} href={n.href} onClick={close}
                     style={{display:'flex', alignItems:'center', gap:12, padding:'13px 18px', textDecoration:'none',
                             background: on ? 'var(--bg-sub)' : 'transparent',
                             borderLeft: `3px solid ${on ? 'var(--orange-500)' : 'transparent'}`}}>
                    <span style={{flex:1, minWidth:0}}>
                      <span style={{display:'block', fontSize:14.5, fontWeight:700, color: on ? 'var(--orange-700)' : 'var(--ink-1)'}}>{n.label}</span>
                      <span style={{display:'block', fontSize:11, color:'var(--ink-3)', marginTop:2}}>{NAV_DESC[n.href]}</span>
                    </span>
                    <span style={{color:'var(--ink-3)', width:14, height:14, display:'inline-block'}}>{Ic.arrow}</span>
                  </a>
                );
              })}
            </nav>

            <div style={{padding:'14px 18px', borderTop:'1px solid var(--border-soft)', display:'flex', flexDirection:'column', gap:8}}>
              {user ? (
                <button className="btn btn-ghost btn-md" onClick={logout} style={{width:'100%', justifyContent:'center', color:'var(--rose-500)'}}>로그아웃</button>
              ) : (
                <>
                  <a href="/signup" className="btn btn-primary btn-md" style={{textDecoration:'none', justifyContent:'center'}}>회원가입</a>
                  <a href="/login"  className="btn btn-ghost btn-md"   style={{textDecoration:'none', justifyContent:'center'}}>로그인</a>
                </>
              )}
              <a href={`tel:${supportPhone().replace(/[^0-9+]/g, "")}`}
                 style={{display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginTop:4,
                         fontSize:11.5, color:'var(--ink-3)', textDecoration:'none'}}>
                <span style={{width:13, height:13, display:'inline-block'}}>{Ic.phone}</span>
                고객지원 <b style={{color:'var(--ink-2)'}}>{supportPhone()}</b>
              </a>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

// ─── Shell ────────────────────────────────────────────────
function PublicHeader({ active }) {
  const { user } = useCurrentUser();
  const narrow = useIsNarrow(860);
  const parts = brandParts(siteName());
  return (
    <header style={{position:'sticky', top:0, zIndex:20, display:'flex', alignItems:'center', padding:'16px clamp(18px, 5vw, 64px)', borderBottom:'1px solid var(--border-soft)', background:'var(--bg)'}}>
      <a href="/" style={{display:'flex', alignItems:'center', gap:10, textDecoration:'none', color:'inherit'}}>
        <div className="brand-mark" style={{width:32, height:32}}>{parts.mark}</div>
        <div>
          <div style={{fontWeight:800, fontSize:15, color:'var(--ink-1)'}}>{parts.name}</div>
          {parts.sub && <div style={{fontSize:10, color:'var(--ink-3)', letterSpacing:'0.06em', marginTop:-2}}>{parts.sub}</div>}
        </div>
      </a>
      {/* 좁은 화면에서는 메뉴 5개 + 인증 버튼이 한 줄에 안 들어가 햄버거 드로어로 접는다. */}
      {!narrow && (
        <nav style={{display:'flex', gap:'clamp(14px, 2vw, 28px)', marginLeft:'clamp(16px, 3vw, 56px)', fontSize:13, fontWeight:500, overflowX:'auto', whiteSpace:'nowrap'}}>
          {PUBLIC_NAV.map(n => (
            <a key={n.href} href={n.href}
              style={{textDecoration:'none', color: active === n.href ? 'var(--orange-600)' : 'var(--ink-2)',
                      fontWeight: active === n.href ? 700 : 500}}>{n.label}</a>
          ))}
        </nav>
      )}
      <div style={{marginLeft:'auto', display:'flex', gap:10, alignItems:'center'}}>
        {narrow ? <MobileNavMenu active={active}/> : (user
          ? <a href={user.default_path} className="btn btn-primary btn-md" style={{textDecoration:'none'}}>내 화면으로 →</a>
          : <>
              <a href="/login"  className="btn btn-ghost btn-md"   style={{textDecoration:'none'}}>로그인</a>
              <a href="/signup" className="btn btn-primary btn-md" style={{textDecoration:'none'}}>회원가입</a>
            </>)}
      </div>
    </header>
  );
}

function PublicShell({ active, children }) {
  return (
    <div className="app" style={{background:'var(--bg)', minHeight:'100vh', overflow:'auto', display:'flex', flexDirection:'column'}}>
      <PublicHeader active={active}/>
      <div style={{flex:1}}>{children}</div>
      <AppFooter/>
    </div>
  );
}

// 페이지 상단 히어로 — 배지 + 제목 + 리드 문단(+ 선택 CTA).
function PageHero({ badge, title, lead, cta }) {
  return (
    <section style={{padding:'clamp(36px, 6vw, 64px) clamp(18px, 5vw, 64px) 40px', borderBottom:'1px solid var(--border-soft)', background:'linear-gradient(180deg, var(--orange-50) 0%, transparent 100%)'}}>
      <div style={{maxWidth:1180, margin:'0 auto'}}>
        <div style={{display:'inline-flex', alignItems:'center', gap:8, padding:'6px 14px', background:'var(--surface)', border:'1px solid var(--border-soft)', color:'var(--orange-700)', borderRadius:999, fontSize:12, fontWeight:700, marginBottom:20}}>
          <span className="dot" style={{background:'var(--orange-500)'}}/> {badge}
        </div>
        <h1 style={{fontSize:'clamp(28px, 4.2vw, 42px)', lineHeight:1.18, letterSpacing:'-0.03em', fontWeight:800, color:'var(--ink-1)', margin:0, maxWidth:820, whiteSpace:'pre-line'}}>{title}</h1>
        <p style={{fontSize:16, lineHeight:1.7, color:'var(--ink-2)', marginTop:20, maxWidth:760}}>{lead}</p>
        {cta && <div style={{display:'flex', gap:10, marginTop:28}}>{cta}</div>}
      </div>
    </section>
  );
}

function Section({ eyebrow, title, sub, children, bg }) {
  return (
    <section style={{padding:'clamp(36px, 5vw, 56px) clamp(18px, 5vw, 64px)', background: bg || 'transparent'}}>
      <div style={{maxWidth:1180, margin:'0 auto'}}>
        {eyebrow && <div style={{fontSize:11, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em', marginBottom:8}}>{eyebrow}</div>}
        {title && <div className="h2" style={{fontSize:26, letterSpacing:'-0.02em'}}>{title}</div>}
        {sub && <div className="muted" style={{fontSize:14, marginTop:8, lineHeight:1.7, maxWidth:760}}>{sub}</div>}
        <div style={{marginTop: title || sub ? 28 : 0}}>{children}</div>
      </div>
    </section>
  );
}

function IconTile({ icon, color = "orange", size = 40 }) {
  return (
    <span style={{width:size, height:size, borderRadius:12, background:`var(--${color}-50)`, color:`var(--${color}-600)`, display:'grid', placeItems:'center', flex:`0 0 ${size}px`, fontSize:size*0.55}}>
      <span style={{width:size*0.55, height:size*0.55, display:'inline-block'}}>{Ic[icon]}</span>
    </span>
  );
}

function FeatureCard({ icon, color, title, desc, points, href }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap href={href} className="card card-pad" style={{borderRadius:18, textDecoration:'none', color:'inherit', display:'flex', flexDirection:'column'}}>
      <IconTile icon={icon} color={color}/>
      <div style={{fontWeight:700, fontSize:15, marginTop:14}}>{title}</div>
      <div style={{fontSize:12.5, color:'var(--ink-3)', lineHeight:1.6, marginTop:6}}>{desc}</div>
      {points && (
        <ul style={{margin:'12px 0 0', paddingLeft:0, listStyle:'none', display:'flex', flexDirection:'column', gap:6}}>
          {points.map((p, i) => (
            <li key={i} style={{display:'flex', gap:7, fontSize:12, color:'var(--ink-2)', lineHeight:1.5}}>
              <span style={{color:'var(--green-600)', flex:'0 0 auto', marginTop:1, width:12, height:12, display:'inline-block'}}>{Ic.check}</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
      {href && <div style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, marginTop:'auto', paddingTop:12}}>화면 열기 →</div>}
    </Wrap>
  );
}

function Steps({ items }) {
  return (
    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:16}}>
      {items.map((s, i) => (
        <div key={i} className="card card-pad" style={{borderRadius:16}}>
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            <span style={{width:28, height:28, borderRadius:9, background:'var(--orange-500)', color:'#fff', display:'grid', placeItems:'center', fontSize:12, fontWeight:800}}>{String(i+1).padStart(2,'0')}</span>
            <div style={{fontWeight:700, fontSize:14}}>{s.t}</div>
          </div>
          <div style={{fontSize:12.5, color:'var(--ink-3)', lineHeight:1.6, marginTop:10}}>{s.d}</div>
        </div>
      ))}
    </div>
  );
}

function Accordion({ items }) {
  const [open, setOpen] = React.useState(0);
  return (
    <div className="card" style={{borderRadius:16, overflow:'hidden'}}>
      {items.map((f, i) => (
        <div key={i} style={{borderTop: i ? '1px solid var(--border-soft)' : 'none'}}>
          <button onClick={() => setOpen(open === i ? -1 : i)}
            style={{width:'100%', textAlign:'left', display:'flex', alignItems:'center', gap:12, padding:'16px 20px', background:'transparent', border:'none', cursor:'pointer', fontSize:14, fontWeight:700, color:'var(--ink-1)'}}>
            <span style={{color:'var(--orange-600)', fontWeight:800}}>Q</span>
            <span style={{flex:1}}>{f.q}</span>
            <span style={{color:'var(--ink-3)', transform: open === i ? 'rotate(180deg)' : 'none', transition:'transform .15s'}}>{Ic.caret}</span>
          </button>
          {open === i && (
            <div style={{padding:'0 20px 18px 46px', fontSize:13, lineHeight:1.8, color:'var(--ink-2)', whiteSpace:'pre-line'}}>{f.a}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// 공개 통계(랜딩과 동일 소스) — 소개/도입 페이지 신뢰 지표.
function useLandingStats() {
  const [stats, setStats] = React.useState(null);
  React.useEffect(() => { fetch("/api/v1/landing/stats").then(r => r.json()).then(setStats).catch(()=>{}); }, []);
  return stats;
}

function StatRow({ stats }) {
  const items = [
    { n: stats?.active_regions ?? '·',                         l: '활성 권역' },
    { n: stats?.total_managers?.toLocaleString() ?? '·',       l: '등록 케어매니저' },
    { n: stats?.partner_orgs?.toLocaleString() ?? '·',         l: '등록 유관기관' },
    { n: stats ? `${stats.success_rate}%` : '·',               l: '평균 매칭 성공률' },
    { n: stats ? fmtDuration(stats.avg_response_mins) : '·',   l: '평균 매칭 응답' },
  ];
  return (
    <div className="card" style={{borderRadius:18, padding:'26px 30px', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:24}}>
      {items.map((s, i) => (
        <div key={i}>
          <div style={{fontSize:28, fontWeight:800, color:'var(--orange-600)', fontVariantNumeric:'tabular-nums', lineHeight:1}}>{s.n}</div>
          <div style={{fontSize:12, color:'var(--ink-3)', marginTop:8, fontWeight:500}}>{s.l}</div>
        </div>
      ))}
    </div>
  );
}

// ─── 문의 폼 ──────────────────────────────────────────────
// kind="adoption"(도입 상담) / "support"(고객지원). 서버 검증과 동일하게
// 연락처는 전화·이메일 중 최소 하나를 요구한다. 접수되면 관리자 문의함으로 간다.
const TOPICS = {
  adoption: ["권역 협회 도입", "요양기관 도입", "지자체·공공 도입", "제휴·연동 문의", "기타"],
  support:  ["로그인·계정 문제", "케어 신청·매칭", "방문 일정·기록", "알림 수신", "개인정보·탈퇴", "기타"],
};

function InquiryForm({ kind, title, desc }) {
  const { user } = useCurrentUser();
  const [f, setF]       = React.useState({ name:"", phone:"", email:"", organization_name:"", topic:"", message:"" });
  const [agree, setAgree] = React.useState(false);
  const [busy, setBusy]   = React.useState(false);
  const [err, setErr]     = React.useState(null);
  const [done, setDone]   = React.useState(false);

  // 로그인 상태면 이름·연락처를 채워 재입력을 줄인다(비로그인은 빈 폼).
  React.useEffect(() => {
    if (!user) return;
    setF(prev => ({ ...prev, name: prev.name || user.name || "", email: prev.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!f.name.trim())    { setErr("성함을 입력해 주세요."); return; }
    if (!f.message.trim()) { setErr("문의 내용을 입력해 주세요."); return; }
    if (!f.phone.trim() && !f.email.trim()) { setErr("연락처(전화 또는 이메일)를 하나 이상 입력해 주세요."); return; }
    if (!agree) { setErr("개인정보 수집·이용에 동의해 주세요."); return; }
    setBusy(true); setErr(null);
    try {
      await api.create("inquiries", {
        kind,
        name: f.name.trim(),
        phone: f.phone.trim(),
        email: f.email.trim(),
        organization_name: f.organization_name.trim(),
        topic: f.topic,
        message: f.message.trim(),
      });
      setDone(true);
    } catch (e2) {
      setErr(apiErrorMessage(e2?.details ? { error: e2.code, details: e2.details, message: e2.serverMessage } : null, e2?.status)
             || e2?.serverMessage || "접수에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card card-pad" style={{borderRadius:18, textAlign:'center', padding:'48px 30px'}}>
        <span style={{width:52, height:52, borderRadius:'50%', background:'var(--green-50)', color:'var(--green-600)', display:'grid', placeItems:'center', margin:'0 auto', fontSize:26}}>
          <span style={{width:26, height:26, display:'inline-block'}}>{Ic.check}</span>
        </span>
        <div style={{fontSize:18, fontWeight:800, marginTop:16}}>문의가 접수되었습니다</div>
        <div className="muted" style={{fontSize:13, lineHeight:1.7, marginTop:10}}>
          담당자가 확인 후 남겨주신 연락처로 회신드립니다.<br/>
          급한 문의는 <b style={{color:'var(--ink-1)'}}>{supportPhone()}</b> (평일 09:00–18:00)로 전화 주세요.
        </div>
        <button className="btn btn-ghost btn-md" style={{marginTop:20}}
          onClick={() => { setDone(false); setF({ name:"", phone:"", email:"", organization_name:"", topic:"", message:"" }); setAgree(false); }}>
          문의 하나 더 남기기
        </button>
      </div>
    );
  }

  return (
    <form className="card card-pad" style={{borderRadius:18, display:'flex', flexDirection:'column', gap:14}} onSubmit={submit}>
      <div>
        <div style={{fontSize:16, fontWeight:800}}>{title}</div>
        <div className="muted" style={{fontSize:12.5, lineHeight:1.6, marginTop:6}}>{desc}</div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12}}>
        <Field label="성함" required>
          <input className="input" value={f.name} onChange={set("name")} placeholder="홍길동" maxLength={60}/>
        </Field>
        <Field label={kind === "adoption" ? "기관 · 권역명" : "소속 (선택)"}>
          <input className="input" value={f.organization_name} onChange={set("organization_name")}
                 placeholder={kind === "adoption" ? "○○권역 요양기관협회" : "기관명 또는 지역"}/>
        </Field>
        <Field label="연락처">
          <PhoneInput value={f.phone} onChange={set("phone")}/>
        </Field>
        <Field label="이메일">
          <input className="input" type="email" value={f.email} onChange={set("email")} placeholder="name@example.com"/>
        </Field>
      </div>

      <Field label={kind === "adoption" ? "도입 유형" : "문의 유형"}>
        <select className="input" value={f.topic} onChange={set("topic")}>
          <option value="">선택해 주세요</option>
          {TOPICS[kind].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>

      <Field label="문의 내용" required>
        <textarea className="input" value={f.message} onChange={set("message")} rows={6} maxLength={4000}
          placeholder={kind === "adoption"
            ? "도입 대상(권역·기관 규모), 희망 일정, 궁금한 점을 적어주세요."
            : "어떤 화면에서 어떤 문제가 있었는지 적어주시면 더 빠르게 도와드릴 수 있습니다."}
          style={{resize:'vertical', lineHeight:1.6, padding:'10px 12px', height:'auto'}}/>
      </Field>

      <label style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:12, color:'var(--ink-2)', lineHeight:1.6, cursor:'pointer'}}>
        <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{marginTop:2}}/>
        <span>
          문의 응대를 위한 개인정보(성함·연락처·문의 내용) 수집·이용에 동의합니다. 수집한 정보는 문의 처리 목적으로만
          사용하며 처리 완료 후 <b>1년</b> 보관 뒤 파기합니다. 자세한 내용은 <a href="/privacy" style={{color:'var(--orange-600)'}}>개인정보처리방침</a>을 확인해 주세요.
        </span>
      </label>

      {err && <div style={{padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-600)', borderRadius:10, fontSize:12.5}}>{err}</div>}

      <button type="submit" className="btn btn-primary btn-lg" disabled={busy} style={{justifyContent:'center'}}>
        {busy ? "접수 중…" : kind === "adoption" ? "도입 상담 신청하기" : "문의 접수하기"}
      </button>
      <div className="muted" style={{fontSize:11, textAlign:'center'}}>
        전화 문의 <b style={{color:'var(--ink-2)'}}>{supportPhone()}</b> · 평일 09:00–18:00 (주말·공휴일 휴무)
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{display:'flex', flexDirection:'column', gap:6}}>
      <span style={{fontSize:12, fontWeight:700, color:'var(--ink-2)'}}>
        {label}{required && <span style={{color:'var(--rose-500)', marginLeft:4}}>*</span>}
      </span>
      {children}
    </label>
  );
}

// 페이지 하단 공통 CTA.
function BottomCta({ title, sub, primary, secondary }) {
  return (
    <Section>
      <div className="card" style={{borderRadius:20, padding:'40px 44px', background:'linear-gradient(120deg, var(--orange-50), var(--surface))', border:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:32, flexWrap:'wrap'}}>
        <div>
          <div className="h2" style={{fontSize:24, letterSpacing:'-0.02em'}}>{title}</div>
          <div className="muted" style={{fontSize:14, marginTop:8, lineHeight:1.6}}>{sub}</div>
        </div>
        <div style={{display:'flex', gap:10}}>
          {secondary && <a href={secondary.href} className="btn btn-ghost btn-lg" style={{textDecoration:'none'}}>{secondary.label}</a>}
          <a href={primary.href} className="btn btn-primary btn-lg" style={{textDecoration:'none'}}>{primary.label}</a>
        </div>
      </div>
    </Section>
  );
}

// ─── 김해 통합돌봄 시스템 구성도 ───────────────────────────
// About 페이지의 '해법' 흐름 바로 아래에 얹는 한 장짜리 시스템 조감도.
// 광주광역시 통합돌봄센터 구성도(장기요양/비장기요양 각 4항목 순번) 관례를 참고해
// 김해 버전으로 옮기되, 이 앱에 실제 심어져 있는 3 버킷(장기요양/비장기요양/병원응급)
// + 코드 체계(LTC/NLT/ER)를 그대로 반영. 카탈로그 값은 Setting::CARE_SERVICE_TYPES_
// DEFAULT 를 그대로 미러링한다 — 공개 페이지라 API fetch/인증 흐름 없이 정적으로 둔다.
// (관리자가 /settings 로 카탈로그를 편집하면 케이스 등록 폼은 즉시 반영되지만, 이 소개
// 다이어그램의 갱신은 다음 배포까지 지연될 수 있다는 트레이드오프를 감수.)
const SYSTEM_DIAGRAM_BUCKETS = [
  { key: 'ltc',       code: 'LTC', title: '장기요양',   sub: '노인장기요양보험 급여', items: [
    { code: 'LTC-01', label: '방문요양' },
    { code: 'LTC-02', label: '방문목욕' },
    { code: 'LTC-03', label: '방문간호' },
    { code: 'LTC-04', label: '주야간보호' },
    { code: 'LTC-05', label: '단기보호' },
    { code: 'LTC-06', label: '복지용구' },
    { code: 'LTC-07', label: '시설급여' },
  ]},
  { key: 'non_ltc',   code: 'NLT', title: '비장기요양', sub: '지자체 통합돌봄 사업', items: [
    { code: 'NLT-01', label: '가사지원' },
    { code: 'NLT-02', label: '이동지원' },
    { code: 'NLT-03', label: '정서지원' },
    { code: 'NLT-04', label: '방문의료' },
    { code: 'NLT-05', label: '응급안전' },
    { code: 'NLT-06', label: '식사배달' },
  ]},
  { key: 'emergency', code: 'ER',  title: '응급·병원',  sub: '병원 응급실·119 연계', items: [
    { code: 'ER-01',  label: '응급실 이송' },
    { code: 'ER-02',  label: '퇴원 후 연계' },
    { code: 'ER-03',  label: '119 협력 안전확인' },
  ]},
];
const SYSTEM_DIAGRAM_PIPELINE = [
  { t: '접수',        d: '시민·기관 채널' },
  { t: '요건 확인',   d: '자격·중복 검증' },
  { t: 'AI 매칭',     d: '6차원 점수화' },
  { t: '후보 검증',   d: '운영자 판단' },
  { t: '서비스 연계', d: '기관·자원 배정' },
  { t: '모니터링',    d: '방문·성과' },
];

// gcare 스타일 라이트 톤. 무지개 gradient 테두리 + 파스텔 카드 + 다크 COD 허브 +
// CSS dashed 흐름 커넥터 + 컬러 chip pipeline. 다이내믹 요소는 pulse·shimmer·flow.
const SD_ANIM_CSS = `
  @keyframes sd-riseIn      { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
  @keyframes sd-pulse       { 0%,100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }
  @keyframes sd-borderFlow  { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
  @keyframes sd-flowRight   { from { background-position: 0 0; }   to { background-position: 16px 0; } }
  @keyframes sd-flowLeft    { from { background-position: 16px 0; } to { background-position: 0 0; } }
  @keyframes sd-flowDown    { from { background-position: 0 0; }   to { background-position: 0 16px; } }

  .sd-role-card     { transition: transform 250ms ease, box-shadow 250ms ease; }
  .sd-role-card:hover { transform: translateY(-3px); box-shadow: 0 16px 40px -12px rgba(20,16,12,0.15); }
  .sd-bucket-card   { transition: transform 250ms ease, box-shadow 250ms ease; }
  .sd-bucket-card:hover { transform: translateY(-3px); box-shadow: 0 16px 40px -12px rgba(20,16,12,0.15); }
  .sd-chip          { transition: transform 200ms ease, box-shadow 200ms ease; }
  .sd-chip:hover    { transform: translateY(-2px); box-shadow: 0 6px 16px -6px rgba(20,16,12,0.2); }

  /* 흐르는 대시 커넥터 — 방향별 3종. currentColor 로 색을 상속받아 사용처에서 지정. */
  .sd-conn-in       { background-image: repeating-linear-gradient(90deg, currentColor 0 6px, transparent 6px 12px); animation: sd-flowLeft  0.9s linear infinite; }
  .sd-conn-out      { background-image: repeating-linear-gradient(90deg, currentColor 0 6px, transparent 6px 12px); animation: sd-flowRight 0.9s linear infinite; }
  .sd-conn-down     { background-image: repeating-linear-gradient(0deg,  currentColor 0 6px, transparent 6px 12px); animation: sd-flowDown  0.9s linear infinite; }
`;

// 파이프라인의 활성 노드를 일정 간격으로 앞으로 이동시키는 훅. 6개를 다 돌면 처음으로
// 순환. 사용자가 탭 밖으로 나가면 브라우저가 requestAnimationFrame 을 스로틀링해
// 자동으로 느려짐 — setInterval 이라 CPU 부담이 크지 않음.
function usePipelineTicker(length: number, intervalMs = 1400) {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % length), intervalMs);
    return () => clearInterval(id);
  }, [length, intervalMs]);
  return idx;
}

// 정수 카운트업. 마운트 시 0 → target 까지 500ms 안에 도달. useMemo 없이도 리렌더
// 부담 없음(값 하나짜리 setState).
function useCountUp(target: number, durationMs = 500) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    let raf: number; const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / durationMs);
      setN(Math.round(target * p));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return n;
}

// 편집체 톤의 시스템 조감도. 컬러는 뉴트럴(먹빛/베이지) + 오렌지 액센트 단일 팔레트로
// 좁혀 시각적 소음을 줄이고, 타이포 위계(라벨 10 · 본문 12 · 강조 14 · 히어로 22/44)로
// 요소 간 관계를 보인다. 카드 상단의 헤더 라인은 magazine masthead 형식 — 왼쪽 seal /
// 중앙 타이틀 / 오른쪽 rev 문자열 — 로 편집물처럼 밀도를 준다.
// 다이내믹 요소:
//   - COD 허브: 외곽 링의 tick 마크가 40s 회전 · 내부 disc 미묘한 pulse
//   - 파이프라인: 활성 노드가 1→6 순환 · 진행률에 비례해 track 채움
//   - 서비스 인벤토리 행: hover 배경 반응
//   - 마운트: 각 섹션이 순차 fade-up
// 버킷별 파스텔 컬러 세트 — 라이트 배경에서 각 그룹이 부드럽게 구분되도록.
const SD_BUCKET_ACCENT: Record<string, { main: string; light: string; ring: string; ink: string }> = {
  ltc:       { main: '#0284c7', light: '#e0f2fe', ring: 'rgba(56,189,248,0.35)',  ink: '#075985' },  // sky
  non_ltc:   { main: '#ea580c', light: '#fff7ed', ring: 'rgba(251,146,60,0.35)',  ink: '#9a3412' },  // orange
  emergency: { main: '#e11d48', light: '#fff1f2', ring: 'rgba(244,63,94,0.35)',   ink: '#9f1239' },  // rose
};

// 파이프라인 6단계 컬러 — gcare 참조 스크린샷의 파스텔 chip 색 순서. 각 단계에 고유
// 색을 부여해 파이프라인이 컬러 시퀀스로 읽힌다.
const SD_PIPELINE_COLORS = [
  { bg: '#dbeafe', ink: '#1d4ed8', dot: '#3b82f6' }, // 접수 — blue
  { bg: '#cffafe', ink: '#0e7490', dot: '#06b6d4' }, // 요건 확인 — cyan
  { bg: '#ede9fe', ink: '#6d28d9', dot: '#8b5cf6' }, // AI 매칭 — violet
  { bg: '#ffe4e6', ink: '#be123c', dot: '#f43f5e' }, // 후보 검증 — rose
  { bg: '#dcfce7', ink: '#166534', dot: '#22c55e' }, // 서비스 연계 — green
  { bg: '#f1f5f9', ink: '#475569', dot: '#94a3b8' }, // 모니터링 — slate
];

// 흐르는 대시 커넥터. direction 별로 방향/색상 달라짐. currentColor 상속 활용.
// 크기: 라인 두께 3px, dash 20px, 화살촉 문자 16px — 시각적 무게감 강화.
function SystemDiagramArrow({ direction }: { direction: 'right' | 'left' | 'down' }) {
  const isDown = direction === 'down';
  const cls = direction === 'right' ? 'sd-conn-out' : direction === 'left' ? 'sd-conn-in' : 'sd-conn-down';
  const arrow = direction === 'right' ? '▶' : direction === 'left' ? '◀' : '▼';
  return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                 flexDirection: isDown ? 'column' : 'row', color:'inherit'}}>
      <div className={cls}
           style={{flex:isDown ? '0 0 48px' : 1, height: isDown ? 48 : 3, width: isDown ? 3 : 'auto',
                   backgroundSize: isDown ? '3px 20px' : '20px 3px', borderRadius:2}}/>
      <span style={{fontSize:16, lineHeight:1, color:'currentColor', fontWeight:800,
                    textShadow:'0 1px 2px rgba(0,0,0,0.08)'}}>{arrow}</span>
    </div>
  );
}

// 좌측 컬럼 하단의 라이브 지표 카드. 마운트 시 count-up + 라이브 도트 pulse.
// 값은 데모 상수(=관제 대시보드가 아닌 마케팅 페이지)로, gcare 랜딩의 "410/14/24"
// 스타일 KPI 미러링. 실제 통계는 별도 /api/v1/landing/stats(useLandingStats) 에서
// 이미 다루므로 여기 값은 시스템 구성 소개용 fixed metric 3종만 얹는다.
function SystemDiagramLiveMetrics() {
  const cases  = useCountUp(1284, 900);   // 진행 케이스
  const today  = useCountUp(38, 700);     // 오늘 매칭
  const days   = useCountUp(11, 600);     // 평균 연계일
  return (
    <div className="sd-role-card"
         style={{flex:'1 1 0', padding:'16px 20px', borderRadius:18,
                 background:'linear-gradient(135deg, #fff 0%, #fff7ed 100%)',
                 border:'1px solid #fb923c33',
                 boxShadow:'0 8px 24px -14px rgba(251,146,60,0.2)',
                 display:'flex', flexDirection:'column'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
        <div style={{fontSize:10.5, letterSpacing:2, color:'#c2410c', fontWeight:800}}>LIVE · 실시간 운영</div>
        <span aria-hidden="true"
              style={{width:6, height:6, borderRadius:'50%', background:'#f97316',
                      boxShadow:'0 0 8px #f97316',
                      animation:'sd-pulse 1.4s ease-in-out infinite'}}/>
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10}}>
        {[
          { label: '진행 케이스', value: cases, suffix: '건' },
          { label: '오늘 매칭',   value: today, suffix: '건' },
          { label: '평균 연계',   value: days,  suffix: '일' },
        ].map(m => (
          <div key={m.label} style={{padding:'10px 8px', borderRadius:12,
                                       background:'rgba(255,255,255,0.7)',
                                       border:'1px solid rgba(251,146,60,0.15)'}}>
            <div style={{display:'flex', alignItems:'baseline', gap:2}}>
              <span style={{fontSize:20, fontWeight:800, color:'#c2410c', fontVariantNumeric:'tabular-nums',
                            letterSpacing:'-0.02em', lineHeight:1}}>
                {m.value.toLocaleString()}
              </span>
              <span style={{fontSize:10, color:'var(--ink-3)', fontWeight:600}}>{m.suffix}</span>
            </div>
            <div style={{fontSize:10, color:'var(--ink-3)', marginTop:4, letterSpacing:0.2}}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SystemDiagram() {
  const activeStep = usePipelineTicker(SYSTEM_DIAGRAM_PIPELINE.length, 1400);

  return (
    <div style={{position:'relative', animation:'sd-riseIn 600ms cubic-bezier(0.16,1,0.3,1)'}}>
      <style>{SD_ANIM_CSS}</style>

      {/* 무지개 gradient 테두리 — 얇은 padding 을 이용한 double-layer 트릭. 안쪽은 라이트 배경. */}
      <div style={{padding:2, borderRadius:24,
                   background:'linear-gradient(90deg, #60a5fa 0%, #a78bfa 20%, #f472b6 40%, #fb923c 60%, #34d399 80%, #60a5fa 100%)',
                   backgroundSize:'300% 100%',
                   animation:'sd-borderFlow 12s linear infinite',
                   boxShadow:'0 20px 60px -20px rgba(20,16,12,0.15)'}}>
        <div style={{background:'#fafaf7', borderRadius:22, padding:'34px 36px 32px', position:'relative', overflow:'hidden'}}>

        {/* HEADER — "김해시 통합돌봄센터" pill 배지 (오렌지→로즈 그라디언트) */}
        <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:6, marginBottom:8}}>
          <div style={{display:'inline-flex', alignItems:'center', gap:10, padding:'10px 22px',
                       borderRadius:999,
                       background:'linear-gradient(135deg, #fb923c 0%, #f43f5e 100%)',
                       color:'#fff', boxShadow:'0 8px 24px -6px rgba(251,146,60,0.4)'}}>
            <span aria-hidden="true"
                  style={{width:22, height:22, borderRadius:'50%', background:'rgba(255,255,255,0.25)',
                          display:'grid', placeItems:'center', fontSize:12, fontWeight:800, color:'#fff'}}>金</span>
            <span style={{fontSize:14.5, fontWeight:800, letterSpacing:'-0.01em'}}>김해시 통합돌봄센터</span>
          </div>
          <div style={{fontSize:11, color:'var(--ink-3)', letterSpacing:1, marginTop:4}}>
            가야왕도 · 시화 매화 &nbsp;·&nbsp; 시민 중심의 대도시 김해
          </div>
        </div>

        {/* BODY — flex 명시 폭 3열 (좌/중/우) + 컬럼 사이 흐르는 대시 커넥터 스트립.
            wrap 허용. 좌우 스택은 flex:1 로 균등, COD 는 고정 280px, 좁아지면 wrap. */}
        <div style={{marginTop:24}}>
          <div style={{display:'flex', flexWrap:'wrap', gap:0, alignItems:'stretch', justifyContent:'center', rowGap:16}}>

            {/* LEFT — Operator + AI 매칭 엔진 + 실시간 지표. flex:1 로 남은 좌측 공간
                채우기. min-width 로 wrap 조건. */}
            <div style={{flex:'1 1 240px', minWidth:240, maxWidth:340, display:'flex', flexDirection:'column', gap:14}}>

              {/* Operator */}
              <div className="sd-role-card"
                   style={{flex:'1 1 0', padding:'18px 20px', borderRadius:18,
                           background:'#fff', border:'1px solid #0284c722',
                           boxShadow:'0 8px 24px -14px rgba(20,16,12,0.12)',
                           display:'flex', flexDirection:'column'}}>
                <div style={{display:'flex', gap:12, alignItems:'flex-start'}}>
                  <div style={{width:44, height:44, borderRadius:'50%', background:'#e0f2fe',
                               display:'grid', placeItems:'center', flex:'0 0 auto', color:'#0284c7'}}>
                    <span style={{width:22, height:22, display:'inline-block'}}>{Ic.user}</span>
                  </div>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:15, fontWeight:800, color:'var(--ink-1)'}}>Operator</div>
                    <div style={{fontSize:11.5, color:'var(--ink-3)', marginTop:2}}>사회복지사 · 건강사</div>
                  </div>
                </div>
                <div style={{marginTop:12, paddingTop:12, borderTop:'1px dashed #0284c733', display:'flex', flexDirection:'column', gap:7}}>
                  {[
                    { d: '접수 · 상담 · 요건 확인' },
                    { d: '자격 검증 · 케이스 개시' },
                    { d: '매칭 결과 검수 · 배정' },
                    { d: '기관 연계 · 사후 관리' },
                  ].map(row => (
                    <div key={row.d} style={{display:'flex', alignItems:'center', gap:8, fontSize:11.5, color:'var(--ink-2)'}}>
                      <span style={{width:5, height:5, borderRadius:'50%', background:'#0284c7', flex:'0 0 auto'}}/>
                      {row.d}
                    </div>
                  ))}
                </div>
              </div>

              {/* AI 매칭 엔진 */}
              <div className="sd-role-card"
                   style={{flex:'1 1 0', padding:'18px 20px', borderRadius:18,
                           background:'#fff', border:'1px solid #db277722',
                           boxShadow:'0 8px 24px -14px rgba(20,16,12,0.12)',
                           display:'flex', flexDirection:'column'}}>
                <div style={{display:'flex', gap:12, alignItems:'flex-start'}}>
                  <div style={{width:44, height:44, borderRadius:'50%', background:'#fce7f3',
                               display:'grid', placeItems:'center', flex:'0 0 auto', color:'#db2777'}}>
                    <span style={{width:22, height:22, display:'inline-block'}}>{Ic.match}</span>
                  </div>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:15, fontWeight:800, color:'var(--ink-1)'}}>AI 매칭 엔진</div>
                    <div style={{fontSize:11.5, color:'var(--ink-3)', marginTop:2}}>최적 서비스 자동 연계</div>
                  </div>
                </div>
                <div style={{marginTop:12, paddingTop:12, borderTop:'1px dashed #db277733'}}>
                  <div style={{fontSize:10, letterSpacing:1.5, color:'var(--ink-3)', fontWeight:700, marginBottom:8}}>6차원 채점</div>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 12px'}}>
                    {['거리', '자격', '가용시간', '부하', '이력', '평점'].map(d => (
                      <div key={d} style={{display:'flex', alignItems:'center', gap:7, fontSize:11.5, color:'var(--ink-2)'}}>
                        <span style={{width:5, height:5, borderRadius:'50%', background:'#db2777'}}/>
                        {d}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 실시간 운영 지표 — 다이내믹 count-up. 좌측 컬럼 하단을 채우면서 우측
                  카탈로그와 높이 균형. 데모 성격의 정적 숫자에 애니메이션만 얹는다. */}
              <SystemDiagramLiveMetrics/>
            </div>

            {/* CONNECTOR 좌→중 — 세로 중앙에 크게 하나. 좌측 스택 전체가 COD 로
                흐름을 이루도록 통합. */}
            <div aria-hidden="true"
                 style={{flex:'0 0 56px', minWidth:56, display:'flex', flexDirection:'column',
                         justifyContent:'center', alignItems:'center', color:'#0284c7'}}>
              <SystemDiagramArrow direction="right"/>
            </div>

            {/* CENTER — Dark COD hub 카드. 고정 폭 280px, wrap 시 100%.
                paddingTop 으로 COD 를 좌우 wing 의 무게중심 아래에 배치. */}
            <div style={{flex:'0 0 280px', maxWidth:'100%', display:'flex', flexDirection:'column', alignItems:'center',
                         paddingTop:40}}>
              <div style={{width:'100%', minHeight:480, borderRadius:20,
                           background:'linear-gradient(160deg, #1e1b16 0%, #2a241f 100%)', color:'#fff',
                           display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                           padding:'40px 24px', position:'relative', boxShadow:'0 16px 40px -16px rgba(20,16,12,0.35)'}}>
                <div style={{fontSize:10, letterSpacing:2.5, opacity:0.55, fontWeight:600}}>CASE OPERATION DESK</div>
                <div style={{fontSize:60, fontWeight:800, letterSpacing:'-0.02em', marginTop:10, lineHeight:1,
                             color:'#fdba74', textShadow:'0 0 30px rgba(251,146,60,0.4)'}}>COD</div>
                <div style={{fontSize:12, opacity:0.7, marginTop:8, letterSpacing:0.5}}>ALL Best Service</div>
                <div style={{display:'flex', flexDirection:'column', gap:10, marginTop:26, width:'100%'}}>
                  {['접수 · 상담 · 요건 확인', 'AI 매칭 · 서비스 연계', '모니터링 · 품질 관리'].map(row => (
                    <div key={row} style={{padding:'9px 14px', background:'rgba(255,255,255,0.06)', borderRadius:10,
                                            fontSize:12, textAlign:'center', border:'1px solid rgba(255,255,255,0.05)'}}>
                      {row}
                    </div>
                  ))}
                </div>
                <div style={{marginTop:14, fontSize:11, color:'#fdba74', letterSpacing:0.5,
                             display:'inline-flex', alignItems:'center', gap:8}}>
                  <span style={{width:6, height:6, borderRadius:'50%', background:'#fdba74', boxShadow:'0 0 8px #fdba74', animation:'sd-pulse 1.6s ease-in-out infinite'}}/>
                  14일 내 서비스 연계 완료 목표
                </div>
              </div>
            </div>

            {/* CONNECTOR 중→우 — 세로 중앙에 크게 하나. 우측 스택 전체가 COD 출력을
                받는 흐름으로 통합. */}
            <div aria-hidden="true"
                 style={{flex:'0 0 56px', minWidth:56, display:'flex', flexDirection:'column',
                         justifyContent:'center', alignItems:'center', color:'#0284c7'}}>
              <SystemDiagramArrow direction="right"/>
            </div>

            {/* RIGHT — LTC + NLT 만 스택 (ER 는 하단 별도 강조 카드로 이동). */}
            <div style={{flex:'1 1 240px', minWidth:240, maxWidth:340, display:'flex', flexDirection:'column', gap:14}}>
              {SYSTEM_DIAGRAM_BUCKETS.filter(b => b.key !== 'emergency').map(b => {
                const a = SD_BUCKET_ACCENT[b.key];
                return (
                  <div key={b.key} className="sd-bucket-card"
                       style={{flex:'1 1 0', padding:'16px 18px', borderRadius:18, background:'#fff',
                               border:`1px solid ${a.ring}`,
                               boxShadow:'0 8px 24px -14px rgba(20,16,12,0.12)',
                               display:'flex', flexDirection:'column'}}>
                    <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10, paddingBottom:10, borderBottom:`1px dashed ${a.ring}`}}>
                      <span style={{padding:'4px 10px', borderRadius:999, background: a.light,
                                    color: a.ink, fontSize:11.5, fontWeight:800, letterSpacing:'-0.01em'}}>
                        {b.title}
                      </span>
                      <span style={{fontSize:10.5, color:'var(--ink-3)'}}>{b.items.length}종</span>
                    </div>
                    <div style={{display:'grid', gridTemplateColumns:'auto 1fr', columnGap:10, rowGap:6, fontSize:12, lineHeight:1.5}}>
                      {b.items.map(it => (
                        <React.Fragment key={it.code}>
                          <span style={{fontSize:10.5, fontWeight:800, color: a.main, letterSpacing:0.4,
                                        fontVariantNumeric:'tabular-nums'}}>{it.code}</span>
                          <span style={{color:'var(--ink-2)'}}>{it.label}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* COD → ER 다운 커넥터 — 중앙 정렬 세로 흐르는 대시 라인 (길게). */}
          <div aria-hidden="true"
               style={{display:'flex', flexDirection:'column', alignItems:'center', marginTop:16, color:'#e11d48'}}>
            <div className="sd-conn-down"
                 style={{width:3, height:72, backgroundSize:'3px 20px', borderRadius:2}}/>
            <span style={{fontSize:22, lineHeight:1, marginTop:4, fontWeight:800,
                          textShadow:'0 2px 4px rgba(225,29,72,0.2)'}}>▼</span>
          </div>

          {/* EMERGENCY — COD 아래로 이동, 가로 전폭 강조 카드. 로즈 그라디언트 + pulse. */}
          {(() => {
            const b = SYSTEM_DIAGRAM_BUCKETS.find(x => x.key === 'emergency')!;
            const a = SD_BUCKET_ACCENT[b.key];
            return (
              <div style={{marginTop:8, position:'relative', zIndex:1}}>
                <div className="sd-bucket-card"
                     style={{padding:'18px 22px', borderRadius:20, background:'#fff',
                             border:`1.5px solid ${a.main}55`,
                             boxShadow:`0 12px 32px -14px ${a.ring}, inset 0 0 0 1px rgba(255,255,255,0.5)`,
                             backgroundImage:`linear-gradient(135deg, #fff 0%, ${a.light} 100%)`,
                             display:'grid', gridTemplateColumns:'auto 1fr', gap:20, alignItems:'center'}}>
                  {/* 좌측 헤더 블록 */}
                  <div style={{display:'flex', alignItems:'center', gap:12, paddingRight:18, borderRight:`1px dashed ${a.ring}`}}>
                    <div style={{width:46, height:46, borderRadius:'50%',
                                 background:`linear-gradient(135deg, ${a.main}, #a855f7)`,
                                 display:'grid', placeItems:'center',
                                 boxShadow:`0 0 0 4px ${a.main}22, 0 8px 20px -6px ${a.ring}`,
                                 animation:'sd-pulse 1.8s ease-in-out infinite'}}>
                      <span style={{fontSize:11, fontWeight:800, color:'#fff', letterSpacing:0.5}}>ER</span>
                    </div>
                    <div>
                      <div style={{fontSize:15, fontWeight:800, color: a.ink, letterSpacing:'-0.01em', display:'flex', alignItems:'center', gap:8}}>
                        {b.title}
                        <span style={{fontSize:9.5, letterSpacing:2, color:'#fff', fontWeight:800, background: a.main, padding:'3px 8px', borderRadius:6}}>URGENT</span>
                      </div>
                      <div style={{fontSize:11, color:'var(--ink-3)', marginTop:3}}>{b.sub} · 24/7 응답</div>
                    </div>
                  </div>
                  {/* 우측 항목 chip 그리드 */}
                  <div style={{display:'grid', gridTemplateColumns:`repeat(${b.items.length}, 1fr)`, gap:10}}>
                    {b.items.map(it => (
                      <div key={it.code}
                           style={{padding:'10px 12px', borderRadius:12,
                                   background:'rgba(255,255,255,0.7)',
                                   border:`1px solid ${a.main}22`}}>
                        <div style={{fontSize:10.5, fontWeight:800, color: a.main, fontVariantNumeric:'tabular-nums', letterSpacing:0.5}}>{it.code}</div>
                        <div style={{fontSize:12, color:'var(--ink-1)', marginTop:3}}>{it.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

        </div>

        {/* PIPELINE — gcare 참조 컬러 chip strip. 활성 단계 pulse 강조. */}
        <div style={{marginTop:34, paddingTop:22, borderTop:'1px dashed var(--border-soft)'}}>
          <div style={{textAlign:'center', fontSize:10.5, letterSpacing:3, fontWeight:800, color:'var(--ink-3)', marginBottom:14}}>COD 6-STEP PIPELINE</div>
          <div style={{display:'flex', justifyContent:'center', alignItems:'center', flexWrap:'wrap', gap:8}}>
            {SYSTEM_DIAGRAM_PIPELINE.map((s, i) => {
              const c = SD_PIPELINE_COLORS[i];
              const isActive = i === activeStep;
              return (
                <React.Fragment key={s.t}>
                  <div className="sd-chip"
                       style={{display:'inline-flex', alignItems:'center', gap:8, padding:'9px 16px',
                               borderRadius:999, background: c.bg, color: c.ink,
                               fontSize:12.5, fontWeight:700, letterSpacing:'-0.01em',
                               boxShadow: isActive ? `0 6px 16px -4px ${c.dot}55, 0 0 0 3px ${c.dot}22` : 'none',
                               transform: isActive ? 'translateY(-2px) scale(1.03)' : 'none',
                               transition:'all 400ms ease'}}>
                    <span style={{width:6, height:6, borderRadius:'50%', background: c.dot,
                                  boxShadow: isActive ? `0 0 10px ${c.dot}` : 'none',
                                  animation: isActive ? 'sd-pulse 1.4s ease-in-out infinite' : 'none'}}/>
                    <span>{i+1}. {s.t}</span>
                  </div>
                  {i < SYSTEM_DIAGRAM_PIPELINE.length - 1 && (
                    <span aria-hidden="true" style={{fontSize:14, color:'var(--ink-3)', fontWeight:600}}>›</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

      </div>
      </div>
    </div>
  );
}

// ─── 1. 서비스 소개 ────────────────────────────────────────
function About() {
  const stats = useLandingStats();
  return (
    <PublicShell active="/about">
      <PageHero
        badge="서비스 소개"
        title={"흩어져 있던 돌봄을\n한 권역 안에서 잇습니다."}
        lead="통합돌봄은 어르신 한 분의 돌봄에 관여하는 모든 사람 — 권역 협회 관리자, 요양·재가기관, 케어매니저, 보호자, 그리고 어르신 본인 — 이 같은 정보를 같은 시점에 보도록 만드는 운영 플랫폼입니다. 전화와 수기 대장으로 오가던 배정·방문·기록을 하나의 흐름으로 묶습니다."
        cta={<>
          <a href="/adopt" className="btn btn-primary btn-lg" style={{textDecoration:'none'}}>도입 상담 신청 →</a>
          <a href="/features" className="btn btn-ghost btn-lg" style={{textDecoration:'none'}}>주요 기능 보기</a>
        </>}
      />

      <Section eyebrow="문제" title="돌봄 현장의 세 가지 단절"
        sub="현장에서 반복되는 비효율은 대부분 '정보가 한곳에 없다'는 데서 출발합니다.">
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:18}}>
          {[
            { i:'phone', c:'rose',  t:'배정이 전화로 돕니다', d:'누가 여유가 있는지, 어느 매니저가 가까운지 모른 채 전화를 돌립니다. 배정 한 건에 몇 시간이 걸리고, 기준도 담당자마다 다릅니다.' },
            { i:'case',  c:'amber', t:'기록이 흩어집니다',   d:'방문 기록은 수첩에, 활력 수치는 메모지에, 일정은 개인 달력에 남습니다. 담당자가 바뀌면 이력이 함께 사라집니다.' },
            { i:'heart', c:'sky',   t:'가족이 알 수 없습니다', d:'오늘 방문이 있었는지, 어르신 상태가 어땠는지 보호자가 확인할 방법이 없어 다시 전화로 묻게 됩니다.' },
          ].map((x,i) => (
            <div key={i} className="card card-pad" style={{borderRadius:18}}>
              <IconTile icon={x.i} color={x.c}/>
              <div style={{fontWeight:700, fontSize:15, marginTop:14}}>{x.t}</div>
              <div style={{fontSize:12.5, color:'var(--ink-3)', lineHeight:1.65, marginTop:8}}>{x.d}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="해법" title="신청부터 방문 기록까지, 끊기지 않는 하나의 흐름" bg="var(--bg-sub)"
        sub="회원가입으로 접수된 케어 신청이 승인·케이스·매칭·방문·기록까지 같은 데이터 위에서 이어집니다. 각 단계의 상태는 관련된 모든 역할이 즉시 확인합니다.">
        <Steps items={[
          { t:'케어 신청', d:'보호자·어르신이 회원가입하며 어르신 정보를 등록하면 케어 신청이 함께 접수됩니다.' },
          { t:'승인 · 케이스', d:'권역 관리자가 신청을 검토해 승인하면 케이스가 열립니다. 모든 승인은 이력으로 남습니다.' },
          { t:'AI 자동매칭', d:'거리·자격·가용시간·부하·이력·평점 6개 차원으로 후보 매니저를 점수화해 추천합니다.' },
          { t:'방문 수행', d:'배정된 매니저가 모바일에서 일정을 확인하고 방문을 시작·종료합니다.' },
          { t:'기록 · 공유', d:'활력·특이사항·사진이 담긴 방문 보고서가 작성되고 보호자에게 즉시 공유됩니다.' },
        ]}/>
      </Section>

      <Section eyebrow="한눈에" title="김해시 통합돌봄 시스템 구성"
        sub="Operator·AI 매칭 엔진이 COD(Case Operation Desk)로 모여 장기요양·비장기요양·병원(응급) 3개 서비스군으로 연계됩니다. 각 서비스에는 안정된 코드가 부여되어 신청·매칭·기록 전 구간에서 동일하게 참조됩니다.">
        <SystemDiagram/>
      </Section>

      <Section eyebrow="사용자" title="다섯 역할, 각자에게 맞는 화면"
        sub="같은 데이터를 쓰지만 화면은 역할마다 다릅니다. 운영자는 데스크톱에서 권역 전체를, 매니저와 가족은 휴대폰에서 자기 일만 봅니다.">
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:18}}>
          <FeatureCard icon="region"  color="orange" title="권역 관리자"    desc="권역 안의 케이스·기관·매니저·승인을 한 화면에서 운영합니다."
            points={["케이스 등록과 매칭 승인", "케어매니저·유관기관 관리", "권역 통계와 리포트"]}/>
          <FeatureCard icon="org"     color="sky"    title="유관기관 담당자" desc="병원·보건소·복지관 등 협력 기관 담당자가 자기 기관 정보를 관리합니다."
            points={["기관 정보 자기 관리", "지도에서 위치·연계 확인", "기관 대시보드"]}/>
          <FeatureCard icon="manager" color="green"  title="케어매니저"      desc="휴대폰만으로 일정 확인부터 방문 기록까지 끝냅니다."
            points={["오늘 일정과 이동 동선", "매칭 제안 수락·거절", "현장에서 방문 보고서 작성"]}/>
          <FeatureCard icon="user"    color="amber"  title="보호자"          desc="부모님의 방문 일정과 상태를 앱에서 확인합니다."
            points={["방문 시작·종료 알림", "방문 기록과 활력 확인", "담당 매니저와 메시지"]}/>
          <FeatureCard icon="heart"   color="rose"   title="어르신 본인"     desc="큰 글씨 홈에서 오늘 일정과 복약을 확인하고 긴급 도움을 요청합니다."
            points={["오늘 방문·복약 안내", "응급 SOS 접수", "쉬운 도움말"]}/>
          <FeatureCard icon="settings" color="sky"   title="시스템 관리자"   desc="전국 권역·계정·권한·감사 로그를 총괄합니다."
            points={["권역/관리자 계정 관리", "권한 매트릭스 설정", "감사 로그와 알림 규칙"]}/>
        </div>
      </Section>

      <Section eyebrow="운영 현황" title="지금 이 순간의 플랫폼" bg="var(--bg-sub)"
        sub="아래 숫자는 데모 데이터가 아니라 현재 운영 중인 데이터베이스 집계입니다.">
        <StatRow stats={stats}/>
      </Section>

      <BottomCta
        title="우리 권역에 어떻게 적용될지 궁금하신가요?"
        sub="권역 규모와 현재 운영 방식을 알려주시면 도입 시나리오를 정리해 드립니다."
        primary={{ href:'/adopt', label:'도입 상담 신청 →' }}
        secondary={{ href:'/care-act-2026', label:'2026 통합돌봄법 알아보기' }}
      />
    </PublicShell>
  );
}

// ─── 2. 주요 기능 ──────────────────────────────────────────
const FEATURE_GROUPS = [
  {
    eyebrow: "매칭 · 운영",
    title: "케이스를 열고, 사람을 잇습니다",
    cards: [
      { icon:'match', color:'orange', title:'AI 자동매칭 (COD)', href:'/cod',
        desc:'거리·자격·가용시간·부하·이력·평점 6개 차원의 가중치 평가로 후보 매니저를 점수화합니다.',
        points:['차원별 점수와 근거를 함께 표시', '가중치는 시스템 설정에서 조정', '직접 배정 시 사유 필수·감사 기록'] },
      { icon:'case', color:'green', title:'케이스 관리', href:'/cases',
        desc:'신청·매칭·돌봄·종결까지 케이스의 상태를 한 곳에서 추적합니다.',
        points:['같은 어르신의 중복 케이스 차단', '종결 케이스는 목록·집계에서 제외', '매니저 간 케이스 이관(승인 경유)'] },
      { icon:'approve', color:'sky', title:'승인 관리', href:'/approval',
        desc:'가입·소속 변경·기관 담당자 이관 등 승인이 필요한 요청을 하나의 큐로 모읍니다.',
        points:['권역별로 분리된 승인 큐', '승인·반려 이력 5년 보존', '처리 결과 자동 반영'] },
    ],
  },
  {
    eyebrow: "현장 · 모바일",
    title: "휴대폰에서 방문이 끝납니다",
    cards: [
      { icon:'schedule', color:'orange', title:'일정 · 방문 관리', href:'/schedule',
        desc:'주간 일정과 방문 상태(예정·진행·완료·취소)를 실시간으로 관리합니다.',
        points:['현장에서 일정 변경·추가', '주간 근무시간 실시간 집계', '배정 권한 가드로 오배정 차단'] },
      { icon:'chart', color:'green', title:'방문 보고서',
        desc:'혈압·맥박·체온 등 활력과 특이사항, 현장 사진을 함께 남깁니다.',
        points:['생리학적 범위를 벗어난 값 입력 차단', '방문 1건당 보고서 1건', '위험 활력은 자동으로 알림 발생'] },
      { icon:'pill', color:'sky', title:'복약 관리',
        desc:'어르신별 복약 일정을 등록하고 복용 여부를 확인합니다.',
        points:['복약 리마인더 알림', '미복약 시 보호자 알림', '중단·삭제 이력 관리'] },
      { icon:'alert', color:'rose', title:'응급 SOS',
        desc:'어르신·보호자가 긴급 상황을 접수하면 즉시 운영자에게 전달됩니다.',
        points:['소방·병원·보건소 자동 매칭', '운영자가 끌 수 없는 긴급 알림', '접수 이력 5년 보존'] },
    ],
  },
  {
    eyebrow: "연결 · 알림",
    title: "필요한 사람에게, 필요한 순간에",
    cards: [
      { icon:'bell', color:'orange', title:'알림 발송',
        desc:'앱 알림함·SMS·이메일 세 채널로 같은 알림을 상황에 맞게 전달합니다.',
        points:['카테고리별 수신 거부 설정', '심야 시간 발송 억제(조용 시간)', '발송 이력과 실패 사유 추적'] },
      { icon:'chat', color:'green', title:'보호자 ↔ 매니저 메시지',
        desc:'담당 매니저와 보호자가 케이스 단위 스레드에서 직접 소통합니다.',
        points:['알림에서 스레드로 바로 이동', '읽음 상태 표시', '개인 연락처 노출 없이 소통'] },
      { icon:'map', color:'sky', title:'지도 · 이동 최적화', href:'/map',
        desc:'전국 유관기관과 매니저 위치를 지도에서 확인하고 동선을 줄입니다.',
        points:['전국 17개 시도 권역 지원', '겹치는 지점 클러스터링', '화면 영역 기준 조회로 대량 데이터 처리'] },
      { icon:'dashboard', color:'amber', title:'설치형 앱(PWA)',
        desc:'홈 화면에 설치해 앱처럼 쓰고, 네트워크가 끊겨도 기본 화면이 열립니다.',
        points:['iOS·안드로이드 홈 화면 설치', '오프라인 폴백 화면', '새 버전 자동 감지'] },
    ],
  },
  {
    eyebrow: "관리 · 신뢰",
    title: "운영을 증명할 수 있게",
    cards: [
      { icon:'chart', color:'orange', title:'리포트 · 통계', href:'/report',
        desc:'권역·기관·매니저 단위 실적을 집계하고 CSV로 내려받습니다.',
        points:['엑셀에서 한글이 깨지지 않는 CSV', '기간·권역별 비교', '리포트 템플릿 관리'] },
      { icon:'settings', color:'green', title:'권한 매트릭스', href:'/settings',
        desc:'역할별로 무엇을 할 수 있는지 화면에서 직접 조정합니다.',
        points:['화면과 API가 같은 권한을 참조', '핵심 권한은 잠금 처리', '변경 시 감사 기록'] },
      { icon:'approve', color:'sky', title:'감사 로그', href:'/audit',
        desc:'누가 언제 무엇을 했는지 기록하고, 중요한 행위는 오래 보관합니다.',
        points:['케이스·승인·권한 변경 5년 보존', '조건에 맞는 행위 발생 시 메일 알림', 'CSV 내보내기'] },
      { icon:'user', color:'rose', title:'회원 · 개인정보',
        desc:'탈퇴와 개인정보 처리를 정책대로 자동화합니다.',
        points:['본인 탈퇴와 관리자 탈퇴 처리', '유예 기간 후 자동 익명화', '권역 밖 데이터 접근 차단'] },
    ],
  },
];

function Features() {
  return (
    <PublicShell active="/features">
      <PageHero
        badge="주요 기능"
        title={"돌봄 운영에 필요한 기능을\n빠짐없이, 한 곳에."}
        lead="매칭·케이스·일정·기록·알림·지도·리포트·감사까지, 권역 운영에 실제로 필요한 기능을 하나의 플랫폼에 담았습니다. 아래 기능은 모두 현재 서비스에서 동작 중입니다."
        cta={<>
          <a href="/signup" className="btn btn-primary btn-lg" style={{textDecoration:'none'}}>회원가입하고 사용해 보기</a>
          <a href="/adopt" className="btn btn-ghost btn-lg" style={{textDecoration:'none'}}>도입 상담 신청</a>
        </>}
      />

      {FEATURE_GROUPS.map((g, gi) => (
        <Section key={gi} eyebrow={g.eyebrow} title={g.title} bg={gi % 2 ? 'var(--bg-sub)' : undefined}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:18}}>
            {g.cards.map((c, i) => <FeatureCard key={i} {...c}/>)}
          </div>
          <div className="muted" style={{fontSize:11.5, marginTop:14}}>
            ⓘ 링크가 있는 기능은 로그인 후 해당 권한이 있는 계정에서 열립니다.
          </div>
        </Section>
      ))}

      <BottomCta
        title="기능을 직접 보시겠어요?"
        sub="역할별 화면과 실제 데이터 흐름을 함께 살펴보는 데모를 안내해 드립니다."
        primary={{ href:'/adopt', label:'데모 · 도입 상담 신청 →' }}
        secondary={{ href:'/support', label:'기능 문의하기' }}
      />
    </PublicShell>
  );
}

// ─── 3. 2026 통합돌봄법 ────────────────────────────────────
function CareAct() {
  return (
    <PublicShell active="/care-act-2026">
      <PageHero
        badge="2026 돌봄통합지원법 시행 대비"
        title={"2026년 3월,\n지역 돌봄의 운영 방식이 바뀝니다."}
        lead="「의료·요양 등 지역 돌봄의 통합지원에 관한 법률」(약칭 돌봄통합지원법)이 2024년 3월 공포되어 2026년 3월 27일 시행됩니다. 살던 곳에서 계속 생활할 수 있도록 의료·요양·돌봄을 통합해 지원하는 체계로, 지자체와 수행기관에는 새로운 운영·기록 책임이 생깁니다."
        cta={<a href="/adopt" className="btn btn-primary btn-lg" style={{textDecoration:'none'}}>시행 준비 상담 신청 →</a>}
      />

      <Section eyebrow="핵심" title="법이 요구하는 것">
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(230px, 1fr))', gap:18}}>
          {[
            { i:'user',     c:'orange', t:'통합 판정 · 개인별 지원계획', d:'대상자의 건강·생활 상태를 통합적으로 판단하고, 그에 맞춘 개인별 지원계획을 세워 관리하도록 합니다.' },
            { i:'org',      c:'green',  t:'다기관 연계 수행',           d:'보건·의료·요양·돌봄 서비스를 제공하는 여러 기관이 하나의 계획 아래 연계해 움직여야 합니다.' },
            { i:'schedule', c:'sky',    t:'제공 이력 관리',             d:'어떤 서비스가 언제 누구에게 제공되었는지 확인할 수 있어야 합니다.' },
            { i:'chart',    c:'amber',  t:'점검 · 환류',               d:'지원계획의 이행 상황을 주기적으로 점검하고 결과를 반영해 조정하는 순환 구조가 필요합니다.' },
          ].map((x,i) => (
            <div key={i} className="card card-pad" style={{borderRadius:18}}>
              <IconTile icon={x.i} color={x.c}/>
              <div style={{fontWeight:700, fontSize:14.5, marginTop:14}}>{x.t}</div>
              <div style={{fontSize:12.5, color:'var(--ink-3)', lineHeight:1.65, marginTop:8}}>{x.d}</div>
            </div>
          ))}
        </div>
        <div style={{marginTop:18, padding:'14px 18px', background:'var(--bg-sub)', borderRadius:12, fontSize:12, color:'var(--ink-3)', lineHeight:1.7}}>
          ⓘ 위 내용은 법률의 취지를 요약한 것으로, 세부 시행 기준은 시행령·시행규칙 및 관계 부처 지침에 따릅니다.
          최신 원문은 <a href="https://www.law.go.kr" target="_blank" rel="noopener noreferrer" style={{color:'var(--orange-600)'}}>국가법령정보센터</a>에서 확인해 주세요.
        </div>
      </Section>

      <Section eyebrow="대응" title="법의 요구사항과 플랫폼 기능" bg="var(--bg-sub)"
        sub="제도가 요구하는 운영 절차를 플랫폼의 어떤 기능이 담당하는지 정리했습니다.">
        <div className="card" style={{borderRadius:16, overflow:'hidden'}}>
          <div style={{overflowX:'auto'}}>
          <table className="table" style={{width:'100%', minWidth:640, borderCollapse:'collapse'}}>
            <thead>
              <tr style={{background:'var(--surface-2)', textAlign:'left'}}>
                <th style={{padding:'12px 18px', fontSize:12, fontWeight:700, color:'var(--ink-3)', width:'30%'}}>제도가 요구하는 것</th>
                <th style={{padding:'12px 18px', fontSize:12, fontWeight:700, color:'var(--ink-3)', width:'30%'}}>플랫폼 기능</th>
                <th style={{padding:'12px 18px', fontSize:12, fontWeight:700, color:'var(--ink-3)'}}>남는 근거</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['대상자 발굴·접수',       '회원가입 연동 케어 신청 · 응급 SOS 접수',   '신청 일시, 신청 경로, 승인 이력'],
                ['상태 파악과 우선순위',   '어르신 위험도(위험·주의·안정) 관리',        '위험도 변경 사유와 변경자 기록(5년)'],
                ['담당자 지정',           'AI 자동매칭 6차원 평가 · 직접 배정',        '배정 근거 점수, 직접 배정 사유(5년)'],
                ['다기관 연계',           '유관기관 마스터 · 지도 기반 연계',          '기관 정보 변경 이력'],
                ['서비스 제공 이력',       '방문 일정 · 방문 보고서(활력·사진)',        '방문 시작·종료 시각, 보고서 원본'],
                ['가족 대상 정보 제공',    '보호자 앱 알림 · 방문 타임라인 · 메시지',   '알림 발송 이력과 수신 상태'],
                ['점검 · 환류',           '권역 리포트 · 통계 · CSV 내보내기',         '기간별 집계와 다운로드 이력'],
                ['개인정보 보호',         '권역 격리 · 권한 매트릭스 · 탈퇴 익명화',   '접근 권한 변경 이력, 감사 로그'],
              ].map((r, i) => (
                <tr key={i} style={{borderTop:'1px solid var(--border-soft)'}}>
                  <td style={{padding:'13px 18px', fontSize:13, fontWeight:700, color:'var(--ink-1)'}}>{r[0]}</td>
                  <td style={{padding:'13px 18px', fontSize:12.5, color:'var(--ink-2)'}}>{r[1]}</td>
                  <td style={{padding:'13px 18px', fontSize:12, color:'var(--ink-3)'}}>{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </Section>

      <Section eyebrow="준비" title="시행 전 점검 항목"
        sub="지금부터 준비하면 시행 시점에 서두르지 않아도 됩니다.">
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:18}}>
          {[
            { t:'우리 권역의 대상자와 자원 현황을 숫자로 파악하고 있는가', d:'대상 어르신 수, 활동 매니저 수, 협력 기관 수를 매달 같은 기준으로 집계할 수 있어야 합니다.' },
            { t:'배정 기준을 설명할 수 있는가',                          d:'"왜 이 매니저인가"를 근거와 함께 남길 수 있어야 점검에 대응할 수 있습니다.' },
            { t:'제공한 서비스의 이력이 남는가',                          d:'수기 대장은 보존·검색·증빙 모두에서 한계가 있습니다.' },
            { t:'개인정보 접근 범위가 통제되는가',                        d:'담당하지 않는 지역·대상자의 정보에 접근할 수 없어야 합니다.' },
          ].map((x,i) => (
            <div key={i} className="card card-pad" style={{borderRadius:16, display:'flex', gap:14}}>
              <span style={{width:26, height:26, borderRadius:8, background:'var(--green-50)', color:'var(--green-600)', display:'grid', placeItems:'center', flex:'0 0 26px', fontSize:14}}>
                <span style={{width:14, height:14, display:'inline-block'}}>{Ic.check}</span>
              </span>
              <div>
                <div style={{fontWeight:700, fontSize:14, lineHeight:1.5}}>{x.t}</div>
                <div style={{fontSize:12.5, color:'var(--ink-3)', lineHeight:1.65, marginTop:6}}>{x.d}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <BottomCta
        title="시행까지 남은 시간, 준비는 지금부터"
        sub="권역 현황을 알려주시면 제도 대응 관점에서 필요한 준비 사항을 함께 정리해 드립니다."
        primary={{ href:'/adopt', label:'도입 상담 신청 →' }}
        secondary={{ href:'/features', label:'기능 살펴보기' }}
      />
    </PublicShell>
  );
}

// ─── 4. 도입 안내 ──────────────────────────────────────────
function Adopt() {
  const stats = useLandingStats();
  return (
    <PublicShell active="/adopt">
      <PageHero
        badge="도입 안내"
        title={"권역 단위로 시작해\n한 달 안에 운영에 올립니다."}
        lead="권역 협회, 요양·재가기관, 지자체 어디서든 도입할 수 있습니다. 기존 데이터를 옮기고 계정을 만들고 담당자 교육을 마치는 데까지 보통 4주가 걸립니다."
        cta={<>
          <a href="#inquiry" className="btn btn-primary btn-lg" style={{textDecoration:'none'}}>도입 상담 신청 →</a>
          <a href="/features" className="btn btn-ghost btn-lg" style={{textDecoration:'none'}}>기능 먼저 보기</a>
        </>}
      />

      <Section eyebrow="도입 대상" title="이런 곳에서 씁니다">
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:18}}>
          <FeatureCard icon="region" color="orange" title="권역 요양기관협회"
            desc="여러 기관을 아우르는 권역 단위 운영 주체."
            points={["권역 전체 케이스·매니저 통합 운영", "기관 간 매칭과 이관", "권역 리포트"]}/>
          <FeatureCard icon="org" color="green" title="요양 · 재가기관"
            desc="자체 케어매니저를 두고 방문 서비스를 제공하는 기관."
            points={["소속 매니저 일정·부하 관리", "방문 기록 표준화", "보호자 소통 창구"]}/>
          <FeatureCard icon="dashboard" color="sky" title="지자체 · 공공"
            desc="지역 돌봄 통합지원 사업을 수행·관리하는 기관."
            points={["지역 자원 현황 파악", "제공 이력 근거 확보", "통계·보고 자료"]}/>
        </div>
      </Section>

      <Section eyebrow="절차" title="도입은 다섯 단계" bg="var(--bg-sub)"
        sub="각 단계마다 담당자가 함께합니다. 기존 대장·명부가 있다면 그대로 가져올 수 있습니다.">
        <Steps items={[
          { t:'상담 신청',   d:'아래 폼으로 권역·기관 규모와 현재 운영 방식을 알려주세요. 영업일 기준 2일 내 회신드립니다.' },
          { t:'현황 진단',   d:'대상자 수, 매니저 수, 협력 기관, 현재 기록 방식을 함께 점검하고 도입 범위를 정합니다.' },
          { t:'환경 구성',   d:'권역과 기관을 등록하고 관리자 계정을 생성합니다. 기존 명부는 일괄 등록으로 옮깁니다.' },
          { t:'담당자 교육', d:'역할별 매뉴얼과 실습 세션을 제공합니다. 운영자 2시간, 매니저 1시간이면 충분합니다.' },
          { t:'운영 시작',   d:'일부 케이스로 시범 운영한 뒤 전체로 확대합니다. 초기 4주간 밀착 지원합니다.' },
        ]}/>
      </Section>

      <Section eyebrow="운영 현황" title="이미 이만큼 쓰이고 있습니다">
        <StatRow stats={stats}/>
      </Section>

      <Section eyebrow="자주 묻는 질문" title="도입 전에 많이 물어보시는 것" bg="var(--bg-sub)">
        <Accordion items={[
          { q: "도입 비용은 어떻게 되나요?",
            a: "권역·기관 규모와 사용 범위에 따라 달라집니다. 상담 신청 시 규모를 알려주시면 산정 기준과 함께 안내드립니다." },
          { q: "기존에 쓰던 명부와 방문 기록을 옮길 수 있나요?",
            a: "엑셀·CSV 형태의 어르신 명부, 매니저 명부, 기관 목록은 일괄 등록으로 옮길 수 있습니다.\n과거 방문 기록은 형식에 따라 이관 가능 여부가 달라지므로 진단 단계에서 함께 확인합니다." },
          { q: "케어매니저가 스마트폰에 익숙하지 않아도 되나요?",
            a: "매니저 화면은 휴대폰 전용으로 설계했고, 하루에 쓰는 기능은 일정 확인·방문 시작/종료·보고서 작성 세 가지입니다.\n홈 화면에 앱처럼 설치해 쓸 수 있고, 1시간 교육이면 현장 투입이 가능합니다." },
          { q: "다른 권역의 정보가 보이지는 않나요?",
            a: "권역 격리가 기본입니다. 케이스·방문·기록·매니저·승인은 모두 자기 권역으로 제한되며, 권역 밖 데이터는 API 단계에서 차단됩니다." },
          { q: "인터넷이 끊기는 곳에서도 쓸 수 있나요?",
            a: "설치형 앱(PWA)이라 이미 열어본 화면은 오프라인에서도 표시됩니다. 다만 새 데이터 저장은 연결이 복구된 뒤에 이뤄집니다." },
          { q: "우리 지역은 아직 권역이 없는데 가능한가요?",
            a: "전국 17개 시도 권역이 이미 등록돼 있어 별도 구축 없이 시작할 수 있습니다. 세부 권역 분리가 필요하면 상담 시 함께 설계합니다." },
        ]}/>
      </Section>

      <Section eyebrow="상담 신청" title="도입 상담을 신청하세요"
        sub="담당자가 확인 후 영업일 기준 2일 내에 연락드립니다. 급하시면 전화가 가장 빠릅니다.">
        <div id="inquiry" style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:24, alignItems:'start'}}>
          <InquiryForm kind="adoption"
            title="권역 · 기관 도입 상담"
            desc="규모와 현재 운영 방식을 함께 적어주시면 더 구체적으로 안내드릴 수 있습니다."/>
          <div style={{display:'flex', flexDirection:'column', gap:16}}>
            <div className="card card-pad" style={{borderRadius:18}}>
              <div style={{fontWeight:700, fontSize:14, marginBottom:12}}>바로 연락하기</div>
              <ContactLine icon="phone" label="전화" value={supportPhone()} sub="평일 09:00–18:00"/>
              <ContactLine icon="chat"  label="이메일" value="support@care.local" sub="24시간 접수 · 영업일 회신"/>
            </div>
            <div className="card card-pad" style={{borderRadius:18, background:'var(--orange-50)', border:'1px solid var(--orange-100)'}}>
              <div style={{fontWeight:700, fontSize:14, color:'var(--orange-700)'}}>상담 시 준비하시면 좋은 것</div>
              <ul style={{margin:'12px 0 0', paddingLeft:18, fontSize:12.5, color:'var(--ink-2)', lineHeight:1.9}}>
                <li>대상 어르신 수(대략)</li>
                <li>활동 중인 케어매니저 수</li>
                <li>협력 중인 병원·복지관 등 기관 수</li>
                <li>현재 사용 중인 기록 방식(수기·엑셀·타 시스템)</li>
                <li>희망 도입 시점</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </PublicShell>
  );
}

function ContactLine({ icon, label, value, sub }) {
  return (
    <div style={{display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderTop:'1px solid var(--border-soft)'}}>
      <IconTile icon={icon} color="orange" size={34}/>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:11, color:'var(--ink-3)'}}>{label}</div>
        <div style={{fontSize:14, fontWeight:800, color:'var(--ink-1)'}}>{value}</div>
      </div>
      <div style={{fontSize:11, color:'var(--ink-3)'}}>{sub}</div>
    </div>
  );
}

// ─── 5. 고객지원 ───────────────────────────────────────────
const SUPPORT_FAQ = {
  "계정 · 로그인": [
    { q: "비밀번호를 잊어버렸어요.",
      a: "로그인 화면의 '비밀번호 찾기'에서 가입한 이메일을 입력하면 재설정 안내를 보내드립니다.\n메일이 오지 않으면 스팸함을 확인하시고, 그래도 없으면 고객지원으로 연락해 주세요." },
    { q: "계정이 잠겼다고 나옵니다.",
      a: "비밀번호를 여러 번 틀리면 보안을 위해 일정 시간 잠깁니다. 잠시 후 다시 시도하시거나, 급하면 관리자에게 잠금 해제를 요청해 주세요." },
    { q: "가입했는데 로그인 후 화면이 비어 있어요.",
      a: "관리자 승인이 필요한 역할(케어매니저·유관기관 담당자)은 승인 완료 후 화면이 채워집니다. 승인 진행 상황은 소속 기관 관리자에게 문의해 주세요." },
  ],
  "케어 신청 · 매칭": [
    { q: "케어 신청은 어디서 하나요?",
      a: "별도 신청 폼은 없습니다. 보호자 또는 어르신으로 회원가입하면서 어르신 정보를 등록하면 케어 신청이 함께 접수됩니다.\n진행 상태는 로그인 후 홈 화면에서 확인할 수 있습니다." },
    { q: "매니저 배정까지 얼마나 걸리나요?",
      a: "권역 관리자의 승인과 매칭 절차를 거칩니다. 지역과 상황에 따라 다르지만 보통 며칠 내에 배정되며, 진행 상태는 홈 화면에 표시됩니다." },
    { q: "배정된 매니저를 바꾸고 싶어요.",
      a: "담당 매니저 변경은 권역 관리자가 처리합니다. 고객지원으로 사유와 함께 연락 주시면 담당 권역으로 전달해 드립니다." },
  ],
  "방문 · 기록": [
    { q: "방문 기록은 어디서 보나요?",
      a: "보호자·어르신은 앱의 '일정'에서 방문 타임라인과 방문 보고서를 확인할 수 있습니다. 활력 수치와 현장 사진도 함께 표시됩니다." },
    { q: "방문 일정을 변경하고 싶어요.",
      a: "담당 매니저에게 앱 메시지로 요청하시면 매니저가 일정을 조정합니다. 긴급한 변경은 전화로 연락해 주세요." },
  ],
  "알림 · 개인정보": [
    { q: "알림을 받고 싶지 않아요.",
      a: "마이페이지 > 알림 설정에서 방문·위험신호·메시지·복약 등 카테고리별로 수신 여부를 끌 수 있습니다.\n다만 응급 상황 알림 등 일부 안전 관련 알림은 끌 수 없습니다." },
    { q: "탈퇴하면 제 정보는 어떻게 되나요?",
      a: "마이페이지에서 직접 탈퇴할 수 있습니다. 탈퇴 즉시 로그인이 차단되고, 유예 기간이 지나면 개인 식별 정보는 자동으로 익명 처리됩니다.\n법령상 보존 의무가 있는 기록은 해당 기간 동안 보관됩니다." },
    { q: "제 정보를 다른 지역 관리자도 볼 수 있나요?",
      a: "아닙니다. 담당 권역 밖의 운영자는 어르신·케이스·방문 기록에 접근할 수 없습니다." },
  ],
};

function Support() {
  const [tab, setTab] = React.useState(Object.keys(SUPPORT_FAQ)[0]);
  return (
    <PublicShell active="/support">
      <PageHero
        badge="고객지원"
        title={"막히는 곳이 있으면\n바로 도와드리겠습니다."}
        lead="자주 묻는 질문에서 먼저 찾아보시고, 해결되지 않으면 아래 폼으로 문의를 남겨주세요. 남겨주신 연락처로 담당자가 회신드립니다."
        cta={<>
          <a href={`tel:${supportPhone().replace(/[^0-9+]/g, "")}`} className="btn btn-primary btn-lg" style={{textDecoration:'none'}}>전화 {supportPhone()}</a>
          <a href="#inquiry" className="btn btn-ghost btn-lg" style={{textDecoration:'none'}}>문의 남기기</a>
        </>}
      />

      <Section eyebrow="문의 채널" title="이렇게 연락하실 수 있습니다">
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:18}}>
          {[
            { i:'phone', c:'orange', t:'전화 상담', v: supportPhone(), d:'평일 09:00–18:00 (점심 12:00–13:00)\n주말·공휴일 휴무' },
            { i:'chat',  c:'green',  t:'이메일',   v:'support@care.local', d:'24시간 접수\n영업일 기준 1일 내 회신' },
            { i:'help',  c:'sky',    t:'온라인 문의', v:'아래 문의 폼', d:'접수 즉시 담당자에게 전달\n연락처로 개별 회신' },
          ].map((x,i) => (
            <div key={i} className="card card-pad" style={{borderRadius:18}}>
              <IconTile icon={x.i} color={x.c}/>
              <div style={{fontWeight:700, fontSize:14.5, marginTop:14}}>{x.t}</div>
              <div style={{fontSize:16, fontWeight:800, color:'var(--orange-600)', marginTop:6}}>{x.v}</div>
              <div style={{fontSize:12, color:'var(--ink-3)', lineHeight:1.7, marginTop:8, whiteSpace:'pre-line'}}>{x.d}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="자주 묻는 질문" title="먼저 찾아보세요" bg="var(--bg-sub)">
        <div style={{display:'flex', gap:8, marginBottom:18, flexWrap:'wrap'}}>
          {Object.keys(SUPPORT_FAQ).map(k => (
            <button key={k} onClick={() => setTab(k)}
              className={tab === k ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"}
              style={{borderRadius:999}}>{k}</button>
          ))}
        </div>
        <Accordion key={tab} items={SUPPORT_FAQ[tab]}/>
      </Section>

      <Section eyebrow="사용 안내" title="역할별 사용 설명서"
        sub="로그인 후 본인 역할의 매뉴얼에서 화면별 사용법을 확인할 수 있습니다.">
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:14}}>
          {[
            { t:'케어매니저 매뉴얼', h:'/m/manual',           d:'일정 확인부터 방문 보고서 작성까지' },
            { t:'보호자 매뉴얼',     h:'/m/guardian-manual',  d:'방문 확인, 알림, 매니저 소통' },
            { t:'어르신 매뉴얼',     h:'/m/elder-manual',     d:'홈 화면, 복약, 도움 요청' },
            { t:'권역 관리자 매뉴얼', h:'/manual/region',     d:'케이스·매칭·승인·리포트 운영' },
            { t:'유관기관 매뉴얼',   h:'/manual/agency',      d:'기관 정보 관리와 연계' },
            { t:'시스템 관리자 매뉴얼', h:'/manual/system',   d:'권역·계정·권한·감사 총괄' },
          ].map((m,i) => (
            <a key={i} href={m.h} className="card card-pad" style={{borderRadius:14, textDecoration:'none', color:'inherit', display:'flex', alignItems:'center', gap:12}}>
              <IconTile icon="help" color="orange" size={34}/>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontWeight:700, fontSize:13.5}}>{m.t}</div>
                <div style={{fontSize:11.5, color:'var(--ink-3)', marginTop:3}}>{m.d}</div>
              </div>
              <span style={{color:'var(--ink-3)', width:14, height:14, display:'inline-block'}}>{Ic.arrow}</span>
            </a>
          ))}
        </div>
        <div className="muted" style={{fontSize:11.5, marginTop:12}}>ⓘ 매뉴얼은 로그인 후 해당 역할 계정에서 열립니다.</div>
      </Section>

      <Section eyebrow="문의 남기기" title="해결되지 않았다면 문의해 주세요" bg="var(--bg-sub)">
        <div id="inquiry" style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:24, alignItems:'start'}}>
          <InquiryForm kind="support"
            title="온라인 문의"
            desc="어떤 화면에서 무엇을 하려다 막혔는지 적어주시면 더 빠르게 확인할 수 있습니다."/>
          <div style={{display:'flex', flexDirection:'column', gap:16}}>
            <div className="card card-pad" style={{borderRadius:18}}>
              <div style={{fontWeight:700, fontSize:14, marginBottom:12}}>문의 전 확인해 주세요</div>
              <ul style={{margin:0, paddingLeft:18, fontSize:12.5, color:'var(--ink-2)', lineHeight:1.9}}>
                <li>사용 중인 기기(휴대폰·PC)와 브라우저</li>
                <li>문제가 생긴 화면 이름</li>
                <li>화면에 표시된 오류 문구</li>
                <li>가입하신 이메일 주소</li>
              </ul>
            </div>
            <div className="card card-pad" style={{borderRadius:18}}>
              <div style={{fontWeight:700, fontSize:14, marginBottom:10}}>정책 문서</div>
              <div style={{display:'flex', flexDirection:'column', gap:8, fontSize:13}}>
                <a href="/privacy" style={{color:'var(--ink-1)', textDecoration:'none'}}>개인정보처리방침 →</a>
                <a href="/terms"   style={{color:'var(--ink-1)', textDecoration:'none'}}>이용약관 →</a>
              </div>
            </div>
            <div className="card card-pad" style={{borderRadius:18, background:'var(--rose-50)', border:'1px solid var(--rose-100)'}}>
              <div style={{fontWeight:700, fontSize:14, color:'var(--rose-600)'}}>응급 상황이라면</div>
              <div style={{fontSize:12.5, color:'var(--ink-2)', lineHeight:1.7, marginTop:8}}>
                생명이 위급한 상황에서는 고객지원이 아니라 <b>119</b>에 즉시 신고해 주세요.
                앱에 로그인한 어르신·보호자는 홈 화면의 <b>긴급 도움 요청(SOS)</b>으로 권역 운영자에게 바로 알릴 수 있습니다.
              </div>
            </div>
          </div>
        </div>
      </Section>
    </PublicShell>
  );
}

export { About, Features, CareAct, Adopt, Support, PUBLIC_NAV, MobileNavMenu, useIsNarrow };
